import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, Clock, Facebook, Instagram, Youtube, Linkedin } from 'lucide-react';
import { Button } from './ui/button';
import CalendlyButton from './CalendlyButton';
import BookConsultationForm from './BookConsultationForm';
import { mockData } from '../mock';
import { applyThemeToDocument } from '../utils/themeService';

const Layout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showConsultation, setShowConsultation] = useState(false);
  const [settings, setSettings] = useState(null);
  const settingsLoadedRef = useRef(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location]);

  // Fetch settings once on mount (not on every navigation)
  useEffect(() => {
    if (settingsLoadedRef.current) return;
    const fetchSettings = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/api/settings`);
        const data = await response.json();
        
        settingsLoadedRef.current = true;
        setSettings(data);
        
        if (data) {
          applyThemeToDocument(data);
          applyGlobalButtonStyles(data);
          if (data.ga_measurement_id && !window.__gaLoaded) {
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${data.ga_measurement_id}`;
            document.head.appendChild(script);
            const inline = document.createElement('script');
            inline.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${data.ga_measurement_id}');`;
            document.head.appendChild(inline);
            window.__gaLoaded = true;
          }
          if (data.favicon_url) {
            let link = document.querySelector("link[rel*='icon']") || document.createElement('link');
            link.type = 'image/x-icon';
            link.rel = 'shortcut icon';
            link.href = data.favicon_url;
            document.head.appendChild(link);
          }
        }
      } catch (error) {
        void 0;
      }
    };
    fetchSettings();
  }, []);

  // Function to apply button styles globally
  const applyGlobalButtonStyles = (data) => {
    const root = document.documentElement;
    
    // Set CSS custom properties for primary buttons
    if (data.button_primary_bg) {
      root.style.setProperty('--button-primary-bg', data.button_primary_bg);
    }
    if (data.button_primary_text) {
      root.style.setProperty('--button-primary-text', data.button_primary_text);
    }
    if (data.button_primary_size) {
      const sizes = {
        small: { padding: '8px 16px', fontSize: '14px' },
        medium: { padding: '12px 24px', fontSize: '16px' },
        large: { padding: '16px 32px', fontSize: '18px' }
      };
      const size = sizes[data.button_primary_size] || sizes.medium;
      root.style.setProperty('--button-primary-padding', size.padding);
      root.style.setProperty('--button-primary-font-size', size.fontSize);
    }
    
    // Set CSS custom properties for secondary buttons
    if (data.button_secondary_bg) {
      root.style.setProperty('--button-secondary-bg', data.button_secondary_bg);
    }
    if (data.button_secondary_text) {
      root.style.setProperty('--button-secondary-text', data.button_secondary_text);
    }
    if (data.button_secondary_size) {
      const sizes = {
        small: { padding: '8px 16px', fontSize: '14px' },
        medium: { padding: '12px 24px', fontSize: '16px' },
        large: { padding: '16px 32px', fontSize: '18px' }
      };
      const size = sizes[data.button_secondary_size] || sizes.medium;
      root.style.setProperty('--button-secondary-padding', size.padding);
      root.style.setProperty('--button-secondary-font-size', size.fontSize);
    }
  };

  const navLinks = [
    { name: 'HOME', path: '/' },
    { name: 'COURSES', path: '/trainings' },
    { name: 'THERAPIES', path: '/therapies' },
    { name: 'UPCOMING PROGRAMMES', path: '/events' },
    { name: 'TEAM', path: '/team' },
    { name: 'TESTIMONIALS', path: '/testimonials' },
    { name: 'VLOG', path: '/vlogs' },
    { name: 'CONTACT', path: '/contact' }
  ];

  // Check if we're on admin pages
  const isAdminPage = location.pathname.startsWith('/admin');
  
  // Check if we're on home page
  const isHomePage = location.pathname === '/';
  
  // Don't show header/footer on admin pages
  if (isAdminPage) {
    return <div className="min-h-screen">{children}</div>;
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className={`fixed w-full top-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white shadow-lg' 
          : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex justify-between items-center transition-all duration-500 ${
            isScrolled ? 'py-3' : isHomePage ? 'py-6' : 'py-4'
          }`}>
            {/* Logo - Show dark logo on hero (not scrolled), regular logo when scrolled */}
            <Link to="/" className="flex items-center space-x-3 flex-shrink-0">
              {settings === null ? (
                // Settings loading - render empty placeholder to prevent flash
                <div className={`transition-all duration-500 ${
                  isHomePage ? 'h-20 md:h-26 lg:h-32' : 'h-16 md:h-18'
                }`} />
              ) : (settings?.logo_url || settings?.logo_dark_url) ? (
                // Custom uploaded logo - use dark logo on hero, regular when scrolled
                <img 
                  src={!isScrolled && isHomePage && settings?.logo_dark_url 
                    ? settings.logo_dark_url 
                    : settings?.logo_url || settings?.logo_dark_url
                  } 
                  alt={settings?.name || "Lyfe Simply"} 
                  className={`w-auto object-contain transition-all duration-500 ${
                    isScrolled 
                      ? 'h-16 md:h-24' 
                      : isHomePage 
                        ? 'h-20 md:h-26 lg:h-32'
                        : 'h-16 md:h-18'
                  } ${!isScrolled && !settings?.logo_dark_url ? 'drop-shadow-2xl brightness-0 invert' : ''}`}
                  style={{ maxWidth: isScrolled ? '320px' : isHomePage ? '520px' : '325px' }}
                />
              ) : (
                // Default logo (only if settings loaded but no logo uploaded)
                <>
                  <div className={`bg-gradient-to-br from-purple-500 to-teal-400 rounded-full flex items-center justify-center transition-all duration-500 ${
                    isScrolled ? 'w-10 h-10 ring-0' : 'w-12 h-12 ring-2 ring-white/30'
                  }`}>
                    <div className={`text-white font-bold transition-all duration-300 ${
                      isScrolled ? 'text-xl' : 'text-2xl'
                    }`}>L</div>
                  </div>
                  <div>
                    <h1 className={`font-bold transition-all duration-500 ${
                      isScrolled ? 'text-xl text-purple-600' : 'text-2xl text-white'
                    }`}>
                      {settings?.name || 'Lyfe Simply'}
                    </h1>
                  </div>
                </>
              )}
            </Link>

            {/* Right - Book Consultation + Hamburger (desktop) */}
            <div className="hidden md:flex items-center space-x-4 flex-shrink-0">
              <button
                data-testid="book-consultation-btn"
                data-size="nav"
                onClick={() => setShowConsultation(true)}
                className={`text-white rounded-full font-semibold hover:opacity-90 transition-all duration-500 uppercase tracking-wider shadow-lg ${
                  isScrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1 pointer-events-none'
                }`}
                style={{ 
                  background: 'linear-gradient(to right, #7c3aed, #14b8a6)', 
                  fontSize: '11px', 
                  padding: '8px 20px',
                  letterSpacing: '1px'
                }}
              >
                Book Consultation
              </button>
              <button
                className="p-2.5 rounded-lg transition-all duration-300 flex items-center justify-center text-yellow-500 hover:text-yellow-600 hover:bg-yellow-50/50"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden ml-auto p-2 rounded-lg transition-all duration-300 text-yellow-500 hover:text-yellow-600 hover:bg-yellow-50/50"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Desktop dropdown menu */}
        {isMenuOpen && (
          <div className="hidden md:block absolute right-2 sm:right-4 top-full mt-2 w-56 sm:w-64 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden z-50">
            <nav className="py-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block py-2.5 sm:py-3 px-4 sm:px-6 transition-all duration-200 text-sm sm:text-base ${
                    location.pathname === link.path
                      ? 'bg-purple-600 text-white font-semibold shadow-md'
                      : 'text-gray-700 hover:bg-purple-50 hover:text-purple-600 font-medium'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>
        )}

        {/* Mobile full-screen menu overlay */}
        {isMenuOpen && (
          <div className="md:hidden fixed inset-0 bg-white z-50 flex flex-col" data-testid="mobile-menu-overlay">
            {/* Mobile menu header */}
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <Link to="/" onClick={() => setIsMenuOpen(false)}>
                {settings?.logo_url ? (
                  <img src={settings.logo_url} alt="Logo" className="h-10 object-contain" />
                ) : (
                  <span className="text-xl font-bold text-purple-600">Lyfe Simply</span>
                )}
              </Link>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-2 rounded-full bg-gray-100 text-gray-600"
              >
                <X size={22} />
              </button>
            </div>

            {/* Mobile nav links */}
            <nav className="flex-1 overflow-y-auto py-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block py-4 px-6 text-lg font-medium transition-colors border-b border-gray-50 ${
                    location.pathname === link.path
                      ? 'text-purple-600 bg-purple-50/50'
                      : 'text-gray-800 active:bg-gray-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Mobile Book Consultation button */}
            <div className="p-5 border-t bg-gray-50">
              <button
                data-testid="mobile-book-consultation-btn"
                data-size="nav"
                onClick={() => { setIsMenuOpen(false); setTimeout(() => setShowConsultation(true), 200); }}
                className="w-full text-white rounded-full font-semibold uppercase tracking-wider py-4 text-sm shadow-lg active:opacity-80 transition-all"
                style={{ background: 'linear-gradient(to right, #7c3aed, #14b8a6)' }}
              >
                Book Consultation
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className={`flex-grow page-transition ${location.pathname === '/' ? '' : 'pt-20'}`}>
        {children}
      </main>

      {/* Floating WhatsApp Button */}
      <a
        href={`https://wa.me/${mockData.site.phone.replace(/[^0-9]/g, '')}`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 group"
      >
        <div className="relative">
          <div className="absolute inset-0 bg-green-500 rounded-full opacity-75" style={{ animation: 'ping 2s cubic-bezier(0, 0, 0.2, 1) infinite' }}></div>
          <div className="relative w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-2xl hover:shadow-green-500/50 transition-all duration-300 hover:scale-110 cursor-pointer">
            <svg 
              className="w-8 h-8 text-white" 
              fill="currentColor" 
              viewBox="0 0 24 24"
              width={32}
              height={32}
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
          </div>
        </div>
        
        {/* Tooltip */}
        <div className="absolute right-20 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          <div className="bg-gray-900 text-white px-4 py-2 rounded-lg shadow-xl whitespace-nowrap">
            <span className="text-sm font-semibold">Chat on WhatsApp</span>
          </div>
        </div>
      </a>

      {/* AI Chatbot - Removed, WhatsApp is sufficient */}
      
      {/* Footer - Sleek Design */}
      <footer style={{ backgroundColor: '#3A3A3A' }} className="text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Logo & About */}
            <div className="lg:col-span-4">
              <Link to="/" className="inline-block mb-4">
                {settings?.logo_dark_url ? (
                  <img 
                    src={settings.logo_dark_url} 
                    alt={settings.name || "Lyfe Simply"} 
                    className="h-24 md:h-32 w-auto"
                  />
                ) : settings?.logo_url ? (
                  <img 
                    src={settings.logo_url} 
                    alt={settings.name || "Lyfe Simply"} 
                    className="h-24 md:h-32 w-auto brightness-0 invert opacity-90"
                  />
                ) : (
                  <h3 className="text-2xl font-bold">{settings?.name || 'Lyfe Simply'}</h3>
                )}
              </Link>
              <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-sm">
                {settings?.footer_text || 'Helping individuals gain power in their lives through transformative therapies and professional training programs.'}
              </p>
              {/* Social Media Links */}
              <div className="flex space-x-4">
                <a href="https://www.facebook.com/lyfesimply" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Facebook">
                  <Facebook size={20} />
                </a>
                <a href="https://www.instagram.com/lyfe_simply/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Instagram">
                  <Instagram size={20} />
                </a>
                <a href="https://www.youtube.com/channel/UCOEkE6jQA293Qk-tZf6-5Vg" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="YouTube">
                  <Youtube size={20} />
                </a>
                <a href="https://linkedin.com/company/lyfe-simply/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="LinkedIn">
                  <Linkedin size={20} />
                </a>
              </div>
            </div>

            {/* Links - Two Columns */}
            <div className="lg:col-span-4">
              <div className="grid grid-cols-2 gap-8">
                {/* Column 1 */}
                <div>
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-4">Navigate</h4>
                  <ul className="space-y-2">
                    {navLinks.slice(0, Math.ceil(navLinks.length / 2)).map((link) => (
                      <li key={link.path}>
                        <Link to={link.path} className="text-gray-400 hover:text-white text-sm transition-colors">
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Column 2 */}
                <div>
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-4">More</h4>
                  <ul className="space-y-2">
                    {navLinks.slice(Math.ceil(navLinks.length / 2)).map((link) => (
                      <li key={link.path}>
                        <Link to={link.path} className="text-gray-400 hover:text-white text-sm transition-colors">
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-4">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-4">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-400 text-sm">
                  <Mail size={16} className="mr-3 text-gray-500" />
                  {settings?.email || mockData.site.email}
                </li>
                <li className="flex items-center text-gray-400 text-sm">
                  <Phone size={16} className="mr-3 text-gray-500" />
                  {settings?.phone || mockData.site.phone}
                </li>
                <li className="flex items-center text-gray-400 text-sm">
                  <Clock size={16} className="mr-3 text-gray-500" />
                  {settings?.timing || mockData.site.timing}
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-700 mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center">
            <p className="text-gray-500 text-xs">
              &copy; {new Date().getFullYear()} {settings?.name || 'Lyfe Simply'}. All rights reserved.
            </p>
            <div className="flex space-x-4 mt-3 sm:mt-0">
              <Link to="/privacy-policy" className="text-gray-500 hover:text-gray-300 text-xs transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="text-gray-500 hover:text-gray-300 text-xs transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Book Consultation Form Modal */}
      {showConsultation && (
        <BookConsultationForm onClose={() => setShowConsultation(false)} />
      )}
    </div>
  );
};

export default Layout;
